import { ApplicationConfig, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { responseInterceptor } from './core/interceptors/response.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { flattenColorMix } from './core/theme/flatten-color-mix';

// Midnight (dark) + Daylight (light). PrimeNG's own colorScheme branches already have
// the right role structure (formField.background, content.background, text.color, etc.
// each point at a numbered surface stop) — we just replace the stop values with our
// palettes. Which branch is live is decided by the `p-dark` class on <html>, toggled by
// ThemeService (light / dark / system).
export const AppTheme = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}',
    },
    colorScheme: {
      // Light surfaces mirror the light tokens in styles.css (--color-base etc.).
      light: {
        surface: {
          0: '#ffffff',
          50: '#f7f8fc',
          100: '#eef0f7',
          200: '#e2e4f0',
          300: '#c9ccdf',
          400: '#9a9ebd',
          500: '#5b6080',
          600: '#4a4e6b',
          700: '#383c58',
          800: '#262a42',
          900: '#1b1e30',
          950: '#0e0f1a',
        },
      },
      dark: {
        surface: {
          0: '#eef0fa',
          50: '#eef0fa',
          100: '#dfe1ee',
          200: '#c7cade',
          300: '#b4b7d1',
          400: '#9296b8',
          500: '#6b6f8f',
          600: '#3a3f5c',
          700: '#2a2e46',
          800: '#1c1f33',
          900: '#151726',
          950: '#1b1e30',
        },
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: '0.75rem',
      },
      // Same brand gradient in both schemes.
      colorScheme: {
        light: {
          root: {
            primary: {
              background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
              hoverBackground: 'linear-gradient(135deg, #818cf8 0%, #38bdf8 100%)',
              activeBackground: 'linear-gradient(135deg, #4f46e5 0%, #0284c7 100%)',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              borderColor: 'transparent',
              hoverBorderColor: 'transparent',
              activeBorderColor: 'transparent',
            },
          },
        },
        dark: {
          root: {
            primary: {
              background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
              hoverBackground: 'linear-gradient(135deg, #818cf8 0%, #38bdf8 100%)',
              activeBackground: 'linear-gradient(135deg, #4f46e5 0%, #0284c7 100%)',
              borderColor: 'transparent',
              hoverBorderColor: 'transparent',
              activeBorderColor: 'transparent',
            },
          },
        },
      },
    },
  },
});

// Aura's color-mix() tints rewritten to rgba() so they render on Chrome 109 / Windows 7
// — see flatten-color-mix.ts. An unresolved one means a PrimeNG upgrade added a
// colour form the flattener doesn't understand; app.config.spec.ts fails on it too.
const { preset: LegacySafeTheme, unresolved } = flattenColorMix(AppTheme);
if (unresolved.length && isDevMode()) {
  console.error('flattenColorMix: left unchanged (will not render on Chrome 109):', unresolved);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, responseInterceptor, errorInterceptor])),
    provideAnimationsAsync(),
    MessageService,
    ConfirmationService,
    providePrimeNG({
      theme: {
        preset: LegacySafeTheme,
        options: {
          // Dark when <html> carries .p-dark — set by the index.html boot script and
          // ThemeService (light / dark / system). Without it the light branch applies.
          darkModeSelector: '.p-dark',

          // Without this, PrimeNG's runtime-injected theme stylesheet is UNLAYERED,
          // and unlayered CSS outranks every cascade layer no matter the specificity.
          // Tailwind utilities on PrimeNG controls — hover states, colours, spacing —
          // therefore did nothing, which is why some controls looked themed and others
          // didn't. Putting PrimeNG in its own layer, ordered ahead of Tailwind's
          // utilities (order declared at the top of styles.css), lets utilities win
          // again while PrimeNG still overrides Tailwind's base/preflight resets.
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, components, primeng, utilities',
          },
        },
      },
    }),
  ],
};
